import { useEnsuredForwardedRef } from "@smartsheet/lodestar-utils";
import { callAllHandlers } from "@smartsheet/lodestar-utils";
import { LogActionId } from "@smartsheet/logging-state-service";
import React, {
    ButtonHTMLAttributes,
    FocusEventHandler,
    forwardRef,
    MouseEvent,
    MouseEventHandler,
    useCallback,
    useMemo,
} from "react";
import { useLoggingDecorator, useLoggingEnabledByDefault, LoggedByDefault, LoggableEvent } from "../../../util/logging";
import { useCombinedThemeMode } from "../../../util/theme/useCombinedThemeMode";
import { useMenuParentContext } from "../context/MenuParentContext";
import { MenuItemBaseStyled } from "../styled/MenuItemBaseStyled";

export interface MenuItemBaseProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "disabled" | "onClick"> {
    /**
     * A `clientId` prop is provided for specified elements, which is a unique string
     * that appears as a data attribute `data-client-id` in the rendered code,
     * serving as a hook for automated tests.
     */
    clientId?: string;

    /**
     * If true, will request to close the whole menu hierarchy when this item
     * is selected.
     *
     * Defaults to true.
     */
    closeOnSelect?: boolean;

    /**
     * If true, this menu item is still focusable but will be aria-disabled and
     * click will not work. Note: Since this is a button underneath, enter and
     * space will be disabled if this is true.
     */
    isDisabled?: boolean;

    /**
     * Handler to be called on click.
     */
    onClick?: (event: MouseEvent<HTMLButtonElement>, log: LoggableEvent<LoggedByDefault>) => Promise<void> | void;
}

export const MenuItemBase = forwardRef<HTMLButtonElement, MenuItemBaseProps>(
    (
        {
            children,
            clientId,
            closeOnSelect = true,
            onClick: providedOnClick,
            onFocus: providedOnFocus,
            onMouseEnter: providedOnMouseEnter,
            onMouseLeave: providedOnMouseLeave,
            isDisabled = false,
            ...rest
        },
        parentRef
    ) => {
        const ref = useEnsuredForwardedRef(parentRef);
        const { closeMenuRootRequested, closeAllSubMenus, focusMode, layout, allowClickPropagation } =
            useMenuParentContext();
        const withLogging = useLoggingDecorator({
            clientId,
            controlType: "btn",
        });
        const onClickWithLogging = useLoggingEnabledByDefault(
            withLogging,
            { actionId: LogActionId.CLICK },
            providedOnClick
        );
        const themeMode = useCombinedThemeMode();

        const onClick: MouseEventHandler<HTMLButtonElement> = useCallback(
            (event) => {
                if (!isDisabled) {
                    if (!allowClickPropagation) {
                        event.stopPropagation();
                    }
                    void onClickWithLogging(event);
                    if (event.defaultPrevented) {
                        return;
                    }
                    if (closeOnSelect) {
                        // This won't be null here - it's provided by the MenuParentContext. It's
                        // just the typings that need to be a little weird so we can detect the
                        // root.
                        closeMenuRootRequested?.();
                    }
                }
            },
            [closeMenuRootRequested, closeOnSelect, isDisabled, onClickWithLogging, allowClickPropagation]
        );

        const onMouseEnter: MouseEventHandler<HTMLButtonElement> = useMemo(
            () =>
                callAllHandlers(providedOnMouseEnter, (event) => {
                    if (event.defaultPrevented) {
                        return;
                    }
                    // If the user is navigating with the keyboard, ignore these events.
                    // This means the mouse can be hovering over an item and it the menu
                    // moves (scrolls, perhaps), the hovered item might get re-focused
                    // instead of the one the keyboard would have navigated the user to
                    if (focusMode === "keyboard") {
                        return;
                    }
                    // For sub-menu-items, we don't want to do anything more if this item is already expanded.
                    if (ref.current?.getAttribute("aria-expanded") === "true") {
                        return;
                    }
                    closeAllSubMenus();
                    ref.current?.focus();
                }),
            [focusMode, closeAllSubMenus, providedOnMouseEnter, ref]
        );

        const onMouseLeave: MouseEventHandler<HTMLButtonElement> = useMemo(
            () =>
                callAllHandlers(providedOnMouseLeave, (event) => {
                    if (event.defaultPrevented) {
                        return;
                    }
                    ref.current?.blur();
                }),
            [providedOnMouseLeave, ref]
        );

        const onFocus: FocusEventHandler<HTMLButtonElement> = useMemo(
            () =>
                callAllHandlers(providedOnFocus, () => {
                    if (ref.current?.getAttribute("aria-expanded") !== "true") {
                        // If the target gets focus again somehow, like if focus leaves the menu for some reason,
                        // we'll close the menu to ensure a more consistent experience.
                        closeAllSubMenus();
                    }
                }),
            [providedOnFocus, ref, closeAllSubMenus]
        );

        return (
            <MenuItemBaseStyled
                role="menuitem"
                {...rest}
                isDisabled={isDisabled}
                aria-disabled={isDisabled || undefined}
                onFocus={onFocus}
                onClick={onClick}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                // Disallows tab focus on these items.
                // We'll manually manage focus inside the menu w/ the arrow keys.
                // A Tab should close the Menu, and focus whatever is next in line after the Menu's target element.
                // A Shift+Tab should do the same, but for the element previous to the Menu's taret element.
                tabIndex={-1}
                data-client-id={clientId}
                ref={ref}
                type="button"
                layout={layout}
                themeMode={themeMode}
            >
                {children}
            </MenuItemBaseStyled>
        );
    }
);

MenuItemBase.displayName = "MenuItem";
